import { Subscription } from '@rails/actioncable';
import {
  BillingLineSide,
  getV1CompaniesCompanyIdSettlementsId,
  getV1CompaniesCompanyIdSettlementsPayables,
  GetV1CompaniesCompanyIdSettlementsPayablesData,
  getV1CompaniesCompanyIdSettlementsReceivables,
  GetV1CompaniesCompanyIdSettlementsReceivablesData,
  getV1CompaniesCompanyIdSettlementsSettlementIdAddOnCharges,
  InvoiceCategoryFilter,
  patchV1CompaniesCompanyIdSettlementsId,
  postV1CompaniesCompanyIdSettlements,
  postV1CompaniesCompanyIdSettlementsBulkExport,
  putV1CompaniesCompanyIdSettlementsSettlementIdApprove,
  putV1CompaniesCompanyIdSettlementsSettlementIdCustomerPay,
  putV1CompaniesCompanyIdSettlementsSettlementIdLinesAdd,
  putV1CompaniesCompanyIdSettlementsSettlementIdLinesRemove,
  putV1CompaniesCompanyIdSettlementsSettlementIdPay,
  putV1CompaniesCompanyIdSettlementsSettlementIdSendSettlement,
  putV1CompaniesCompanyIdSettlementsSettlementIdUnapprove,
  putV1CompaniesCompanyIdSettlementsSettlementIdUndoPay,
  Settlement_Read,
  SettlementStateEvent,
  UserBulkExportSettlementsChannel_Read,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { omit } from 'lodash-es';
import { useCallback } from 'react';

import { EXPORT_ROW_LIMIT } from '~constants/consts';
import { SettlementDTO } from '~formsShared/components/SettlementFormFields';
import {
  DraftSettlementsParty,
  getDraftSettlementsPartyFilterName,
} from '~hooks/useSettlements/draftSettlementsParty';
import {
  BulkSettlementExportResult,
  Settlement,
  SettlementLine,
} from '~hooks/useSettlements/models';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink, PaginationQuery } from '~services/pagination';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import {
  getDefaultSettlementStates,
  SettlementSearchParamName,
  SettlementSearchParams,
  SettlementSort,
  settlementStatesByFilter,
  SettlementStatusFilter,
} from '~store/SettlementsStore';
import { alert, AlertTypes } from '~types/AlertTypes';
import { downloadCsvFile } from '~utils/csv/csvDownload';
import { exportRowLimitAlert } from '~utils/csv/exportRowLimitAlert';
import { transformSettlementsToCSV } from '~utils/csv/settlementCsvExport';
import { fetchAllApiPages, TooManyRecordsError } from '~utils/fetchAllPages';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { AddOnCharge } from '../useAddOns/models';

const settlementStateTransitionRequests = {
  [SettlementStateEvent.APPROVE]: putV1CompaniesCompanyIdSettlementsSettlementIdApprove,
  [SettlementStateEvent.CUSTOMER_PAY]:
    putV1CompaniesCompanyIdSettlementsSettlementIdCustomerPay,
  [SettlementStateEvent.PAY]: putV1CompaniesCompanyIdSettlementsSettlementIdPay,
  [SettlementStateEvent.SEND_SETTLEMENT]:
    putV1CompaniesCompanyIdSettlementsSettlementIdSendSettlement,
  [SettlementStateEvent.UNAPPROVE]:
    putV1CompaniesCompanyIdSettlementsSettlementIdUnapprove,
  [SettlementStateEvent.UNDO_PAY]: putV1CompaniesCompanyIdSettlementsSettlementIdUndoPay,
};

export type SettlementStateTransition = keyof typeof settlementStateTransitionRequests;

export function isSettlementStateTransition(
  stateEvent: string,
): stateEvent is SettlementStateTransition {
  return Object.hasOwn(settlementStateTransitionRequests, stateEvent);
}

const draftSettlementsReadBySide = {
  [BillingLineSide.PAYABLE]: getV1CompaniesCompanyIdSettlementsPayables,
  [BillingLineSide.RECEIVABLE]: getV1CompaniesCompanyIdSettlementsReceivables,
} satisfies Record<BillingLineSide, unknown>;

// Results only release the export button; rows keep updating through the settlement
// update channel, so a missed broadcast loses the summary toast and nothing else.
const BULK_EXPORT_RESULT_TIMEOUT_IN_MS = 5 * 60 * 1000;
const BULK_EXPORT_SUBSCRIPTION_TIMEOUT_IN_MS = 5 * 1000;

interface FetchSettlementsPageParams {
  companyId: string;
  cursor?: string;
  limit: number;
  link?: PaginationLink;
  signal: AbortSignal;
}

interface FetchDraftSettlementsPageParams extends FetchSettlementsPageParams {
  party: DraftSettlementsParty;
  side: BillingLineSide;
}

export default function useSettlements() {
  const { settlementsStore, toasterStore, userStore } = useStores();

  // Probe = the exact query the grid would issue right after clearing filters
  // (search + default states, no dates/user filters), capped at one row. Powers
  // the "Clear filters and search again" hint. Failures stay silent (matches the
  // Dispatch probe): the hint simply doesn't show.
  const checkUnfilteredSettlements = useCallback(
    async (
      companyId: string,
      category: InvoiceCategoryFilter,
      searchQuery: string,
      signal: AbortSignal,
    ) => {
      try {
        const isPayables = category === InvoiceCategoryFilter.PAYABLES;
        const fetchFunction = isPayables
          ? getV1CompaniesCompanyIdSettlementsPayables
          : getV1CompaniesCompanyIdSettlementsReceivables;

        const query: PaginationQuery & SettlementSearchParams = {
          'page[limit]': 1,
          [SettlementSearchParamName.SEARCH_QUERY]: searchQuery,
          [SettlementSearchParamName.SETTLEMENT_STATES]: getDefaultSettlementStates(),
        };

        const response = await fetchFunction({
          path: { 'company-id': companyId },
          query,
          signal,
        });

        if (signal.aborted) {
          return;
        }

        settlementsStore.setHasUnfilteredResults(response.data.data.length > 0);
      } catch {
        if (!signal.aborted) {
          settlementsStore.setHasUnfilteredResults(false);
        }
      }
    },
    [],
  );

  // Resolves null when the probe fails, so the clear-filters probe still runs.
  const checkSettlementsOnOtherDates = useCallback(
    async (
      companyId: string,
      category: InvoiceCategoryFilter,
      searchParams: SettlementSearchParams,
      signal: AbortSignal,
    ) => {
      try {
        const isPayables = category === InvoiceCategoryFilter.PAYABLES;
        const fetchFunction = isPayables
          ? getV1CompaniesCompanyIdSettlementsPayables
          : getV1CompaniesCompanyIdSettlementsReceivables;

        const response = await fetchFunction({
          path: { 'company-id': companyId },
          query: {
            'page[limit]': 1,
            ...omit(searchParams, [
              SettlementSearchParamName.SETTLEMENT_CATEGORY,
              SettlementSearchParamName.START_DATE,
              SettlementSearchParamName.END_DATE,
            ]),
          },
          signal,
        });

        return response.data.data.length > 0;
      } catch (error) {
        if (signal.aborted) {
          throw error;
        }

        return null;
      }
    },
    [],
  );

  const createSettlement = useCallback(
    async (companyId: string, data: SettlementDTO): Promise<Settlement> => {
      try {
        const response = await postV1CompaniesCompanyIdSettlements({
          path: { 'company-id': companyId },
          body: Settlement.deparse(data),
        });

        return Settlement.parse(response.data.data);
      } catch (error) {
        return connection.handleRequestError(
          error,
          t('error_messages.settlements.failed_to_create'),
        );
      }
    },
    [],
  );

  const addLinesToSettlement = useCallback(
    async (companyId: string, settlementId: string, lines: SettlementLine[]) => {
      try {
        const response = await putV1CompaniesCompanyIdSettlementsSettlementIdLinesAdd({
          path: { 'company-id': companyId, 'settlement-id': settlementId },
          body: { lines },
        });
        const updatedSettlement = Settlement.parse(response.data.data);

        return updatedSettlement;
      } catch (error) {
        return connection.handleRequestError(
          error,
          t('error_messages.settlements.failed_to_add_lines'),
        );
      }
    },
    [],
  );

  const removeLinesFromSettlement = useCallback(
    async (
      companyId: string,
      settlementId: string,
      lines: SettlementLine[],
      confirmVoid = false,
    ) => {
      try {
        const response = await putV1CompaniesCompanyIdSettlementsSettlementIdLinesRemove({
          path: { 'company-id': companyId, 'settlement-id': settlementId },
          body: { lines, ...(confirmVoid ? { confirm_void: true } : {}) },
        });
        const updatedSettlement = Settlement.parse(response.data.data);

        return updatedSettlement;
      } catch (error) {
        return connection.handleRequestError(
          error,
          t('error_messages.settlements.failed_to_remove_lines'),
        );
      }
    },
    [],
  );

  const getDraftSettlementsForParty = useCallback(
    async ({
      companyId,
      cursor,
      limit,
      link,
      party,
      side,
      signal,
    }: FetchDraftSettlementsPageParams) => {
      const partyFilterName = getDraftSettlementsPartyFilterName(party, side);

      if (!partyFilterName) {
        throw new Error(`Draft settlements of a ${party.type} party cannot be read`);
      }

      const response = await draftSettlementsReadBySide[side]({
        path: { 'company-id': companyId },
        query: {
          'page[limit]': limit,
          [SettlementSearchParamName.SETTLEMENT_STATES]:
            settlementStatesByFilter[SettlementStatusFilter.PENDING],
          [partyFilterName]: [party.id],
          ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
        },
        signal,
      });

      return {
        data: response.data.data.map((settlement) => Settlement.parse(settlement)),
        pagination: extractPagination(response),
      };
    },
    [],
  );

  const getSettlementsByCompanyId = useCallback(
    async ({ companyId, cursor, limit, link, signal }: FetchSettlementsPageParams) => {
      // Snapshot the probe inputs together with the category: the probe must
      // describe the request THIS fetch issued, not whatever the store holds
      // when a slow response finally lands (filters may change mid-flight).
      const requestedSearchParams = settlementsStore.searchParams;
      const category =
        requestedSearchParams[SettlementSearchParamName.SETTLEMENT_CATEGORY];

      if (!category) {
        throw new Error('Settlements category not set.');
      }

      const searchQuery = requestedSearchParams[SettlementSearchParamName.SEARCH_QUERY];
      const hasActiveNonSearchParams = settlementsStore.hasActiveNonSearchParams;
      const { clearingFiltersOnlyDropsDates, userChosenDateScopeTerm } = settlementsStore;

      // The flag resets here, NOT in setSearchParams: DriverPaySettlements calls
      // setSearchParams near-every render, which would permanently suppress the hint.
      settlementsStore.setHasUnfilteredResults(false);

      const isPayables = category === InvoiceCategoryFilter.PAYABLES;
      const fetchFunction = isPayables
        ? getV1CompaniesCompanyIdSettlementsPayables
        : getV1CompaniesCompanyIdSettlementsReceivables;

      const { orderBy, sortBy } = settlementsStore.sortOption;
      let sortQuery:
        | Pick<
            NonNullable<GetV1CompaniesCompanyIdSettlementsPayablesData['query']>,
            'sort[created_at]' | 'sort[payable_for_name]'
          >
        | Pick<
            NonNullable<GetV1CompaniesCompanyIdSettlementsReceivablesData['query']>,
            'sort[created_at]' | 'sort[customer_account][name]'
          > = {
        'sort[created_at]': sortBy,
      };
      if (orderBy === SettlementSort.FOR) {
        sortQuery = isPayables
          ? { 'sort[payable_for_name]': sortBy }
          : { 'sort[customer_account][name]': sortBy, 'sort[created_at]': 'desc' };
      }

      const query: PaginationQuery & SettlementSearchParams = {
        'page[limit]': limit,
        // Omit category from searchParams since it is already in the URL and not accepted as a query param.
        ...omit(requestedSearchParams, [SettlementSearchParamName.SETTLEMENT_CATEGORY]),
        ...sortQuery,
      };

      if (link && cursor) {
        query[`page[${link}]`] = cursor;
      }

      const response = await fetchFunction({
        path: { 'company-id': companyId },
        query,
        signal,
      });

      const settlements = response.data.data.map((settlement) =>
        Settlement.parse(settlement),
      );

      const searchTerm = searchQuery?.trim() ?? '';
      const searchFoundNothingOnFirstPage =
        !link && settlements.length === 0 && Boolean(searchTerm);
      const shouldCheckOtherDates =
        !signal.aborted &&
        searchFoundNothingOnFirstPage &&
        Boolean(
          requestedSearchParams[SettlementSearchParamName.START_DATE] ||
          requestedSearchParams[SettlementSearchParamName.END_DATE],
        ) &&
        searchTerm !== userChosenDateScopeTerm;

      const otherDatesProbeFoundMatches =
        shouldCheckOtherDates &&
        (await checkSettlementsOnOtherDates(
          companyId,
          category,
          requestedSearchParams,
          signal,
        ));
      // A filter change reaches the store a render before the grid aborts this request,
      // so a stale answer must not drop the dates the user just picked.
      const storeStillHoldsRequestedParams =
        JSON.stringify(settlementsStore.searchParams) ===
        JSON.stringify(requestedSearchParams);
      const shouldExtendToAllDates =
        otherDatesProbeFoundMatches === true && storeStillHoldsRequestedParams;
      const unfilteredProbeRepeatsOtherDatesProbe =
        shouldCheckOtherDates &&
        clearingFiltersOnlyDropsDates &&
        otherDatesProbeFoundMatches === false;

      const shouldProbeForUnfilteredResults =
        otherDatesProbeFoundMatches !== true &&
        !unfilteredProbeRepeatsOtherDatesProbe &&
        !signal.aborted &&
        searchFoundNothingOnFirstPage &&
        hasActiveNonSearchParams;

      if (shouldProbeForUnfilteredResults) {
        void checkUnfilteredSettlements(companyId, category, searchQuery ?? '', signal);
      }

      return {
        data: settlements,
        pagination: extractPagination(response),
        shouldExtendToAllDates,
      };
    },
    [checkSettlementsOnOtherDates, checkUnfilteredSettlements],
  );

  const getAllSettlementAddOnCharges = useCallback(
    async (companyId: string, settlementId: string): Promise<AddOnCharge[]> => {
      try {
        const addOnCharges = await fetchAllApiPages(
          (query) =>
            getV1CompaniesCompanyIdSettlementsSettlementIdAddOnCharges({
              path: { 'company-id': companyId, 'settlement-id': settlementId },
              query,
            }),
          AddOnCharge.parse,
        );
        settlementsStore.setAddOnChargesBySettlementId(settlementId, addOnCharges);

        return addOnCharges;
      } catch (error) {
        return connection.handleRequestError(
          error,
          t('error_messages.settlements.failed_to_fetch_add_ons'),
        );
      }
    },
    [],
  );

  const subscribeToSettlementsRTU = useCallback(
    (companyId: string, onSettlementUpdate: (settlement: Settlement) => void) => {
      const cable = connection.realTimeConnection;
      const channel = realTimeChannels.SettlementUpdateChannel;

      return cable?.subscriptions?.create?.(
        { channel, company_id: companyId },
        {
          connected() {},
          disconnected() {},
          received: ({ data }: { data: Settlement_Read }) => {
            onSettlementUpdate(Settlement.parse(data));
          },
        },
      );
    },
    [],
  );

  const getSettlementById = useCallback(
    async (companyId: string, settlementId: string) => {
      try {
        const response = await getV1CompaniesCompanyIdSettlementsId({
          path: { 'company-id': companyId, id: settlementId },
        });

        return Settlement.parse(response.data.data);
      } catch (error) {
        return connection.handleRequestError(
          error,
          t('error_messages.settlements.failed_to_fetch'),
        );
      }
    },
    [],
  );

  const transitionSettlementState = useCallback(
    async (
      companyId: string,
      settlementId: string,
      transition: SettlementStateTransition,
    ) => {
      try {
        const response = await settlementStateTransitionRequests[transition]({
          path: { 'company-id': companyId, 'settlement-id': settlementId },
        });

        return Settlement.parse(response.data.data);
      } catch (error) {
        return connection.handleRequestError(
          error,
          t('error_messages.settlements.failed_to_update_settlement_state'),
          [422],
        );
      }
    },
    [],
  );

  const updateSettlement = useCallback(
    async (
      companyId: string,
      settlementId: string,
      data: SettlementDTO,
    ): Promise<Settlement> => {
      try {
        const response = await patchV1CompaniesCompanyIdSettlementsId({
          path: { 'company-id': companyId, id: settlementId },
          body: Settlement.deparseUpdate(data),
        });

        return Settlement.parse(response.data.data);
      } catch (error) {
        return connection.handleRequestError(
          error,
          t('error_messages.settlements.failed_to_update'),
        );
      }
    },
    [],
  );

  const exportSettlementsCsv = useCallback(
    async (category: InvoiceCategoryFilter) => {
      try {
        const companyId = getEffectiveUserCompanyId(userStore);
        const isPayables = category === InvoiceCategoryFilter.PAYABLES;
        const fetchFunction = isPayables
          ? getV1CompaniesCompanyIdSettlementsPayables
          : getV1CompaniesCompanyIdSettlementsReceivables;

        const filterQuery = omit(settlementsStore.searchParams, [
          SettlementSearchParamName.SETTLEMENT_CATEGORY,
        ]);
        const allSettlements = await fetchAllApiPages(
          (query) =>
            fetchFunction({
              path: { 'company-id': companyId },
              query: { ...filterQuery, ...query },
            }),
          Settlement.parse,
          { maxRecords: EXPORT_ROW_LIMIT },
        );

        if (allSettlements.length === 0) {
          toasterStore.push(alert(t('approvals.export.no_data'), AlertTypes.info));
          return;
        }

        const csvData = transformSettlementsToCSV(allSettlements, category);
        const categoryLabel = isPayables ? 'payables' : 'receivables';
        const dateForFilename = dayjs().format('YYYY-MM-DD');
        const filename = `${userStore.userCompany.legalName}_settlements_${categoryLabel}_${dateForFilename}.csv`;
        downloadCsvFile(csvData, filename);

        toasterStore.push(
          alert(
            t('approvals.export.success', { count: allSettlements.length }),
            AlertTypes.success,
          ),
        );
      } catch (error) {
        if (error instanceof TooManyRecordsError) {
          toasterStore.push(exportRowLimitAlert());

          return;
        }

        connection.handleRequestError(
          error,
          t('error_messages.settlements.failed_to_export_csv'),
        );
        throw error;
      }
    },
    [settlementsStore, toasterStore, userStore],
  );

  const bulkExportSettlements = useCallback(
    async (companyId: string, settlementIds: string[]) => {
      try {
        await postV1CompaniesCompanyIdSettlementsBulkExport({
          path: { 'company-id': companyId },
          body: { settlement_ids: settlementIds },
        });
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.settlements.failed_to_bulk_export'),
        );
      }
    },
    [],
  );

  // The broadcast is never replayed, so the caller must await `subscribed` before
  // sending the request. Only a broadcast whose ids all belong to this request counts:
  // the channel is per user, so another tab's export arrives on it too. Resolves null
  // on timeout.
  const trackBulkExportSettlementsResult = useCallback(
    (userId: string, submittedSettlementIds: string[]) => {
      const submittedIds = new Set(submittedSettlementIds);
      let settle: (result: BulkSettlementExportResult | null) => void = () => {};
      const result = new Promise<BulkSettlementExportResult | null>((resolve) => {
        settle = resolve;
      });
      let markSubscribed: () => void = () => {};
      const subscribed = new Promise<void>((resolve) => {
        markSubscribed = resolve;
      });
      let subscription: Subscription | null = null;
      let resultTimeoutId: number | null = null;
      let subscribedTimeoutId: number | null = null;

      const finish = (outcome: BulkSettlementExportResult | null) => {
        if (resultTimeoutId !== null) {
          window.clearTimeout(resultTimeoutId);
        }
        if (subscribedTimeoutId !== null) {
          window.clearTimeout(subscribedTimeoutId);
        }
        subscription?.unsubscribe?.();
        markSubscribed();
        settle(outcome);
      };

      subscription =
        connection.realTimeConnection?.subscriptions?.create?.(
          { channel: realTimeChannels.UserBulkExportSettlementsChannel, id: userId },
          {
            connected: () => {
              if (subscribedTimeoutId !== null) {
                window.clearTimeout(subscribedTimeoutId);
              }
              markSubscribed();
            },
            received: (data: UserBulkExportSettlementsChannel_Read) => {
              const parsedResult = BulkSettlementExportResult.parse(data);
              const resultIds = parsedResult.settlementIds;
              const resultBelongsToThisRequest =
                resultIds.length > 0 && resultIds.every((id) => submittedIds.has(id));

              if (resultBelongsToThisRequest) {
                finish(parsedResult);
              }
            },
          },
        ) ?? null;
      // A subscription that never confirms must not block the export forever.
      subscribedTimeoutId = window.setTimeout(
        markSubscribed,
        BULK_EXPORT_SUBSCRIPTION_TIMEOUT_IN_MS,
      );
      resultTimeoutId = window.setTimeout(
        () => finish(null),
        BULK_EXPORT_RESULT_TIMEOUT_IN_MS,
      );

      return { cancel: () => finish(null), result, subscribed };
    },
    [],
  );

  return {
    addLinesToSettlement,
    bulkExportSettlements,
    createSettlement,
    exportSettlementsCsv,
    getAllSettlementAddOnCharges,
    getDraftSettlementsForParty,
    getSettlementById,
    getSettlementsByCompanyId,
    removeLinesFromSettlement,
    subscribeToSettlementsRTU,
    trackBulkExportSettlementsResult,
    transitionSettlementState,
    updateSettlement,
  };
}
